import React, { useContext, useState, useEffect } from 'react';
import moment from 'moment-timezone';
import {
  OrderPhysicianPick,
  OrderVerbalDatePick,
  VerifyOrderButtons,
  OrderConvoPick,
  OrderType,
  CheckCPFOrder,
  Loader,
} from '@mummssoftware/common-ui';
import {
  isSameDate,
  getOverrideAgency,
  isAgencyUserDoctorRoleGroup,
} from '@mummssoftware/utils/utils';
import { formatDateMMDDYY } from '@mummssoftware/utils/formatters';
import {
  OrdersGlobalContext,
  VFGlobalContext,
  GlobalContext,
  OPEN_WIDGET,
} from '@mummssoftware/global-context';
import { isFreqOrder } from '@mummssoftware/utils/orders/orders-utils';
import {
  CarePlanFrequency,
  error,
  success,
} from '@mummssoftware/utils/constants';
import { CPF, PRN } from '@mummssoftware/utils/orders/orders-constants';
import {
  SET_CONVO_TYPE,
  SET_DISPLAY_CONFIRMATION,
  SET_CREATE_ORDER,
  SET_ORDER_VERIFIED,
  SET_CHECK_ORDER_PHYSICIAN,
  SET_CHECK_VERBAL_ORDER_DATE,
  SET_ORDER_LOADER,
} from '@mummssoftware/global-context/OrdersGlobalContext';
import { SET_ORDERS_MODE } from '@mummssoftware/global-context/VFGlobalContext';
import {
  getMinVerbalDate,
  conversationTypes,
  mapOrderName,
} from '@mummssoftware/utils/orders';
import { Context } from '../../context';
import { CheckMenuWrapper, CheckOrderInfo } from './WrapperDiv';
import { getOrderVerification } from './utils';

export const ConfirmOrder = () => {
  const {
    convoType,
    verbalDate,
    admitPhysician,
    orderType,
    orderVerified,
    checkVerbalDate,
    checkAdmitPhysician,
    dispatchOrders,
    orderLoader,
    cancelOrderWorkflow,
  } = useContext(OrdersGlobalContext);
  const {
    doctorsGlobal,
    patientGlobal,
    patientDateRestrictions,
    dispatch: dispatchGlobal,
  } = useContext(GlobalContext);
  const { VForderData, resetVFOrderWorkflow, dispatchVF } = useContext(
    VFGlobalContext,
  );
  const { currentUser } = useContext(Context);

  const medNPOrderDoctorBins: string = window.env
    ?.medNPOrderDoctorBins as string;
  const medPAOrderDoctorBins: string = window.env
    ?.medPAOrderDoctorBins as string;
  const isPhysicianRoleGroup: boolean = isAgencyUserDoctorRoleGroup(
    currentUser,
    medNPOrderDoctorBins,
    medPAOrderDoctorBins,
  ) as boolean;

  /**
   * agency can override the order verification process
   */
  const overrideVerifOrderBins: string = window.env
    ?.overrideVerificationOrderBins as string;
  // if true NO verification step
  const isOverrideVerifOrderAgency = getOverrideAgency(
    currentUser,
    overrideVerifOrderBins,
  );

  /**
   * Verbal Date Minimum
   */
  const referralDate =
    patientGlobal && moment((patientGlobal as mumms.patient).referralDate);
  // referralDate is the MIN date for VERBAL DATE for LOC and LC

  const verbalDateMin: any = getMinVerbalDate(
    referralDate,
    patientDateRestrictions,
    orderType,
  );

  /**
   * Confirm Orders State
   */
  const [verbalDateSeverity, setVerbalDateSeverity] = useState<mumms.Severity>(
    null,
  );

  const handleVerbalDateChange = (verbaldate: any) => {
    dispatchOrders({
      type: SET_CHECK_VERBAL_ORDER_DATE,
      payload: verbaldate,
    });
    handleVerbalDateSeverity(verbaldate);
  };
  const handleVerbalDateSeverity = (verbaldate: any) => {
    if (verbaldate && verbalDate && isSameDate(verbaldate, verbalDate)) {
      setVerbalDateSeverity(success);
    } else {
      setVerbalDateSeverity(error);
    }
  };
  // check against admitPhysician
  const [doctorSeverity, setDocSeverity] = useState<mumms.Severity>(null);
  const [physMenuOpen, setPhysMenuOpen] = useState(false);

  const handlePhysicianChange = (physician?: any) => {
    dispatchOrders({
      type: SET_CHECK_ORDER_PHYSICIAN,
      payload: physician,
    });
    handlePhysicianSeverity(physician);
    setPhysMenuOpen(false);
  };
  const handlePhysicianSeverity = (physician?: any) => {
    // display admitPhysician.label if error
    if (
      physician &&
      admitPhysician &&
      physician.personNumber === admitPhysician.personNumber
    ) {
      setDocSeverity(success);
    } else {
      setDocSeverity(error);
    }
  };
  const onPhysFocus = () => {
    setPhysMenuOpen(true);
  };

  useEffect(() => {
    if (isOverrideVerifOrderAgency && verbalDate && admitPhysician) {
      dispatchOrders({
        type: SET_CHECK_ORDER_PHYSICIAN,
        payload: admitPhysician,
      });
      dispatchOrders({
        type: SET_CHECK_VERBAL_ORDER_DATE,
        payload: verbalDate,
      });
      setVerbalDateSeverity(success);
      setDocSeverity(success);
    }
  }, [isOverrideVerifOrderAgency, verbalDate, admitPhysician]);

  // VERBAL DATE modify details and come back to the page
  useEffect(() => {
    if (checkVerbalDate && verbalDate) {
      if (isSameDate(checkVerbalDate, verbalDate)) {
        setVerbalDateSeverity(success);
      } else {
        setVerbalDateSeverity(error);
      }
    }
  }, [checkVerbalDate, verbalDate]);

  // DOCTOR  modify details and come back to the page
  useEffect(() => {
    if (checkAdmitPhysician && admitPhysician) {
      if (checkAdmitPhysician.personNumber === admitPhysician.personNumber) {
        setDocSeverity(success);
      } else {
        setDocSeverity(error);
      }
    }
  }, [admitPhysician, checkAdmitPhysician]);

  useEffect(() => {
    const verification = getOrderVerification(
      orderType,
      VForderData,
      verbalDateSeverity,
      doctorSeverity,
      isPhysicianRoleGroup,
    );
    if (verification) {
      dispatchOrders({
        type: SET_ORDER_VERIFIED,
        payload: true,
      });
    } else {
      dispatchOrders({
        type: SET_ORDER_VERIFIED,
        payload: false,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doctorSeverity, verbalDateSeverity]);
  /**
   * Global Orders functions
   */
  const onCancelFrequency = () => {
    resetVFOrderWorkflow();
  };

  const onCancelClick = () => {
    cancelOrderWorkflow();

    if (isFreqOrder(orderType)) {
      onCancelFrequency();
    }
  };

  const onModifyClick = () => {
    dispatchGlobal({
      type: OPEN_WIDGET,
      payload: {
        componentName: CarePlanFrequency,
      },
    });
    dispatchOrders({
      type: SET_DISPLAY_CONFIRMATION,
      payload: false,
    });
    dispatchVF({
      type: SET_ORDERS_MODE,
      payload: true,
    });
  };

  const onOrderVerified = () => {
    dispatchOrders({
      type: SET_ORDER_VERIFIED,
      payload: false,
    });
    dispatchOrders({
      type: SET_CREATE_ORDER,
      payload: true,
    });
    dispatchOrders({
      type: SET_ORDER_LOADER,
      payload: true,
    });
  };

  const handleConversationChange = (conversation?: any) => {
    dispatchOrders({
      type: SET_CONVO_TYPE,
      payload: conversation,
    });
  };

  /**
   * Global Approval Orders UI
   * When isPhysicianRoleGroup: no need for OrderType, OrderVerbalDatePick, OrderConvoPick, OrderPhysicianPick
   */
  const getApprovalComponents = () => {
    if (isPhysicianRoleGroup) {
      return <></>;
    }
    return (
      <>
        <OrderType orderName={(mapOrderName as any)[orderType as any]} />
        <OrderConvoPick
          value={convoType}
          options={conversationTypes}
          handleConversationChange={handleConversationChange}
        />
        <OrderVerbalDatePick
          verbalDate={checkVerbalDate}
          handleVerbalDateChange={handleVerbalDateChange}
          maximumDate={Date.now()}
          minimumDate={verbalDateMin}
          severity={verbalDateSeverity}
          error={
            verbalDateSeverity === error && verbalDate
              ? formatDateMMDDYY(verbalDate)
              : undefined
          }
        />
        <OrderPhysicianPick
          checkDoctor={(checkAdmitPhysician as mumms.Doctor) || undefined}
          doctorsOptions={(doctorsGlobal as mumms.Doctor[]) || []}
          handlePhysicianChange={handlePhysicianChange}
          severity={doctorSeverity}
          onFocus={onPhysFocus}
          menuIsOpen={physMenuOpen}
          openMenuOnClick
          openMenuOnFocus
          error={
            doctorSeverity === error && admitPhysician
              ? admitPhysician.label
              : undefined
          }
        />
      </>
    );
  };

  /**
   * Global Orders UI
   */
  const getFrequencyOrderComponents = () => (
    <>
      <CheckCPFOrder VFOrderData={VForderData} />
    </>
  );
  const getCheckComponents = (
    ordertype: mumms.DischargeEntityType | mumms.EntityOrderType | null,
  ) => {
    switch (ordertype) {
      case PRN:
      case CPF:
        return getFrequencyOrderComponents();
      default:
        return <></>;
    }
  };

  return (
    <CheckMenuWrapper>
      {orderLoader && <Loader />}
      <VerifyOrderButtons
        onCancelClick={onCancelClick}
        onOrderVerified={onOrderVerified}
        onModifyClick={onModifyClick}
        orderVerified={orderVerified}
        orderType={orderType}
      />
      <CheckOrderInfo
        id="orders-confirm"
        $isPhysicianRoleGroup={isPhysicianRoleGroup}
      >
        {getApprovalComponents()}
        {getCheckComponents(orderType)}
      </CheckOrderInfo>
    </CheckMenuWrapper>
  );
};
